import { motion } from 'framer-motion';
import Hero from '../components/home/Hero';
import EditionHighlights from '../components/home/EditionHighlights';
import PageTransition from '../components/common/PageTransition';
import { FaWhatsapp, FaBookOpen, FaImages, FaVideo, FaPhone } from 'react-icons/fa';
import SEO from '../components/common/SEO';

const features = [
  {
    icon: '📚',
    title: 'Fun Stories',
    description: 'Engaging tales that spark imagination and a love for reading in every issue.',
    color: '#7B2FBE',
    bg: 'rgba(123,47,190,0.07)',
    border: 'rgba(123,47,190,0.15)',
  },
  {
    icon: '🧩',
    title: 'Brain Puzzles',
    description: 'Challenging puzzles and games beautifully designed to make learning exciting!',
    color: '#1565C0',
    bg: 'rgba(21,101,192,0.07)',
    border: 'rgba(21,101,192,0.15)',
  },
  {
    icon: '🎨',
    title: 'Creative Corner',
    description: 'Art, crafts, and creative activities to express each child\'s unique ideas.',
    color: '#F57C00',
    bg: 'rgba(245,124,0,0.07)',
    border: 'rgba(245,124,0,0.15)',
  },
  {
    icon: '🌍',
    title: 'World Learning',
    description: 'Age-appropriate news, culture, and global awareness stories every week.',
    color: '#3CB044',
    bg: 'rgba(60,176,68,0.07)',
    border: 'rgba(60,176,68,0.15)',
  },
  {
    icon: '🏆',
    title: 'Fun Contests',
    description: 'Regular competitions where young readers can win exciting prizes and certificates!',
    color: '#D32F2F',
    bg: 'rgba(211,47,47,0.07)',
    border: 'rgba(211,47,47,0.15)',
  },
  {
    icon: '🦕',
    title: 'Dino Adventures',
    description: 'Adventures with our friendly mascot — tiny dino exploring the big world!',
    color: '#00897B',
    bg: 'rgba(0,137,123,0.07)',
    border: 'rgba(0,137,123,0.15)',
  },
];

const quickLinks = [
  {
    icon: <FaBookOpen />,
    emoji: '📖',
    title: 'Read Samples',
    description: 'Flip through our latest issues',
    path: '/samples',
    color: '#7B2FBE',
    bg: 'linear-gradient(135deg, #F3E5FF, #E8EAFF)',
  },
  {
    icon: <FaImages />,
    emoji: '🖼️',
    title: 'Gallery',
    description: 'See our amazing moments',
    path: '/gallery',
    color: '#1565C0',
    bg: 'linear-gradient(135deg, #E3F2FD, #E8F5E9)',
  },
  {
    icon: <FaVideo />,
    emoji: '🎥',
    title: 'Videos',
    description: 'Watch fun learning content',
    path: '/videos',
    color: '#D32F2F',
    bg: 'linear-gradient(135deg, #FFEBEE, #FFF3E0)',
  },
  {
    icon: <FaPhone />,
    emoji: '💌',
    title: 'Contact Us',
    description: 'Get in touch today',
    path: '/contact',
    color: '#3CB044',
    bg: 'linear-gradient(135deg, #E8F5E9, #FFF8E1)',
  },
];

const Home = () => {
  return (
    <PageTransition>
      <SEO
        title="Children's Weekly Magazine"
        description="India's favorite weekly children's magazine packed with fun stories, brain puzzles, creative activities, global learning, and dino adventures."
        keywords="children magazine, kids puzzles, children stories, kids learning, weekly activity book, tiny times, learning games for kids, read stories kids"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Periodical",
          "name": "The Tiny Times",
          "issn": "Pending",
          "url": "https://the-tiny-times.web.app/",
          "description": "Weekly fun stories, puzzles & learning for children ages 4-15.",
          "inLanguage": "en",
          "publisher": {
            "@type": "NewsMediaOrganization",
            "name": "The Tiny Times",
            "url": "https://the-tiny-times.web.app/",
            "logo": {
              "@type": "ImageObject",
              "url": "https://the-tiny-times.web.app/logo.png"
            },
            "sameAs": [
              "https://facebook.com/tinytimes",
              "https://instagram.com/tinytimes",
              "https://twitter.com/tinytimes",
              "https://youtube.com/tinytimes"
            ]
          }
        }}
      />
      <div className="min-h-screen">

        {/* Hero Section */}
        <Hero />

        {/* Features Section */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-14"
            >
              <div className="inline-block mb-4 px-4 py-1.5 rounded-full font-display font-semibold text-sm"
                style={{ background: 'rgba(60,176,68,0.1)', color: '#1E7C24', border: '1px solid rgba(60,176,68,0.2)' }}>
                🎉 What's Inside Every Issue
              </div>
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4"
                style={{ color: '#1A1F2E' }}>
                Why Kids Love{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #3CB044, #1565C0)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  The Tiny Times!
                </span>
              </h2>
              <p className="text-lg md:text-xl text-neutral-gray-dark max-w-2xl mx-auto">
                Every week brings new adventures, puzzles, and discoveries!
              </p>
              <div className="mt-5 mx-auto w-24 h-1 rounded-full"
                style={{ background: 'linear-gradient(90deg, #3CB044, #1565C0, #7B2FBE)' }} />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="rounded-2xl p-6 cursor-default"
                  style={{
                    background: feature.bg,
                    border: `1px solid ${feature.border}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  }}
                >
                  <div className="text-5xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-display font-bold mb-2" style={{ color: feature.color }}>
                    {feature.title}
                  </h3>
                  <p className="text-neutral-gray-dark leading-relaxed text-sm md:text-base">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Edition Highlights */}
        <section
          className="py-20 md:py-28"
          style={{
            background: 'linear-gradient(160deg, #F0FFF4 0%, #EFF6FF 50%, #F5F0FF 100%)',
          }}
        >
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-14"
            >
              <div className="inline-block mb-4 px-4 py-1.5 rounded-full font-display font-semibold text-sm"
                style={{ background: 'rgba(21,101,192,0.1)', color: '#1565C0', border: '1px solid rgba(21,101,192,0.2)' }}>
                📚 Our Editions
              </div>
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>
                Crafted for Every Age
              </h2>
              <p className="text-lg text-neutral-gray-dark max-w-2xl mx-auto">
                Two thoughtfully designed editions — each perfectly tailored for your child's world.
              </p>
            </motion.div>
            <EditionHighlights />
          </div>
        </section>

        {/* Quick Links Grid */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>
                Explore More 🗺️
              </h2>
              <p className="text-lg text-neutral-gray-dark max-w-xl mx-auto">
                Everything you need to know about The Tiny Times
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {quickLinks.map((link, index) => (
                <motion.a
                  key={index}
                  href={link.path}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  whileHover={{ scale: 1.05, y: -6 }}
                  whileTap={{ scale: 0.97 }}
                  className="rounded-2xl p-6 text-center cursor-pointer"
                  style={{
                    background: link.bg,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                    border: '1px solid rgba(255,255,255,0.8)',
                  }}
                >
                  <div className="text-5xl mb-3">{link.emoji}</div>
                  <h3 className="text-lg font-display font-bold mb-1" style={{ color: link.color }}>
                    {link.title}
                  </h3>
                  <p className="text-sm text-neutral-gray-dark">{link.description}</p>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* WhatsApp CTA Banner */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto text-center rounded-3xl p-10 md:p-14 relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #1E7C24, #145218)',
                boxShadow: '0 20px 60px rgba(30,124,36,0.35)',
              }}
            >
              {/* Background decoration */}
              {['📚', '⭐', '🌈', '🦕'].map((e, i) => (
                <motion.span
                  key={i}
                  className="absolute text-4xl opacity-10 pointer-events-none select-none"
                  style={{
                    left: `${i * 28}%`,
                    top: i % 2 === 0 ? '10%' : '70%',
                  }}
                  animate={{ y: [0, -12, 0], rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 3 + i, repeat: Infinity, delay: i * 0.4 }}
                >{e}</motion.span>
              ))}

              <motion.div
                animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                className="text-7xl mb-5"
              >
                🎁
              </motion.div>
              <h2 className="text-3xl md:text-5xl font-display font-bold text-white mb-4">
                Ready to Join the Adventure?
              </h2>
              <p className="text-lg text-white/85 mb-8 max-w-xl mx-auto">
                Subscribe today and give your child the joy of weekly learning!
                Contact us on WhatsApp for subscription details.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="https://wa.me/919618377750"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl text-white"
                  style={{ background: '#25D366', boxShadow: '0 6px 24px rgba(37,211,102,0.4)' }}
                >
                  <FaWhatsapp className="text-2xl" />
                  Subscribe Now
                </motion.a>
                <motion.a
                  href="/editions"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl"
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    border: '2px solid rgba(255,255,255,0.35)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  View Editions 📖
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

export default Home;